import { getNeighborhoods } from '@repo/api';
import { createSlice } from '../factory';
import { runWithDedup } from '../utils';
/**
 * Core neighborhoods slice without persistence by default.
 * Platforms can override by registering a slice with the same name
 * and different persistence configuration.
 */
export const neighborhoodsSlice = createSlice({
    name: 'neighborhoods',
    initialState: {
        neighborhoods: [],
        neighborhoodsByBorough: {},
        neighborhoodAccordionData: {},
        lastFetched: null,
    },
    persist: {
        keys: ['neighborhoods', 'neighborhoodsByBorough', 'neighborhoodAccordionData', 'lastFetched'],
    },
    actions: ({ get, setSlice, getSlice }) => ({
        fetchNeighborhoods: async (city = 'New York') => {
            const apiGetNeighborhoods = getNeighborhoods;
            const key = 'neighborhoods.fetchNeighborhoods';
            const dedupKey = `${key}:${city}`;
            const exec = async () => {
                const fetchResults = await apiGetNeighborhoods({ city, pageSize: 1000 }, 
                // Next.js cache options - force cache for 7 days. Only used on web, won't break mobile
                {
                    cache: 'force-cache',
                    next: { revalidate: 7 * 24 * 60 * 60 }, // 7 days in seconds
                });
                const neighborhoods = fetchResults?.results;
                // Build children map and byId in one pass
                const byId = new Map();
                const childrenMap = new Map(); // parentId -> childrenIds
                for (const n of neighborhoods) {
                    byId.set(n.id, n);
                    if (n.parentId) {
                        if (!childrenMap.has(n.parentId))
                            childrenMap.set(n.parentId, []);
                        childrenMap.get(n.parentId).push(n.id);
                    }
                }
                // Enhance neighborhoods with childrenIds and group by borough in a single pass
                const enhancedNeighborhoods = [];
                const neighborhoodsByBorough = {};
                const enhancedById = new Map();
                for (const n of neighborhoods) {
                    const enhanced = {
                        ...n,
                        childrenIds: childrenMap.get(n.id) || [],
                    };
                    enhancedNeighborhoods.push(enhanced);
                    enhancedById.set(enhanced.id, enhanced);
                    const borough = enhanced.borough;
                    if (!neighborhoodsByBorough[borough])
                        neighborhoodsByBorough[borough] = [];
                    neighborhoodsByBorough[borough].push(enhanced);
                }
                // Build UI-friendly accordion data, flattened to two levels and sorted
                const neighborhoodAccordionData = {};
                // Memoized top-level ancestor lookup constrained by borough
                const rootCache = new Map(); // key: `${id}|${borough}` -> rootId or null
                const findRootInBorough = (id, borough) => {
                    const key = id + '|' + borough;
                    if (rootCache.has(key))
                        return rootCache.get(key);
                    let current = enhancedById.get(id);
                    if (!current) {
                        rootCache.set(key, null);
                        return null;
                    }
                    let safety = 0;
                    while (current && current.parentId && safety < 50) {
                        const parent = enhancedById.get(current.parentId);
                        if (!parent || parent.borough !== borough) {
                            current = null;
                            break;
                        }
                        current = parent;
                        safety++;
                    }
                    const rootId = current ? current.id : null;
                    rootCache.set(key, rootId);
                    return rootId;
                };
                for (const [borough, boroughNeighborhoods] of Object.entries(neighborhoodsByBorough)) {
                    const topLevelParents = boroughNeighborhoods.filter(n => n.parentId == null);
                    const topLevelParentIds = new Set(topLevelParents.map(n => n.id));
                    const childrenByParent = {};
                    const standalone = [];
                    for (const n of boroughNeighborhoods) {
                        if (n.parentId == null)
                            continue;
                        const rootId = findRootInBorough(n.id, borough);
                        if (rootId && topLevelParentIds.has(rootId)) {
                            if (!childrenByParent[rootId])
                                childrenByParent[rootId] = [];
                            childrenByParent[rootId].push(n);
                        }
                        else {
                            standalone.push(n);
                        }
                    }
                    const parents = [...topLevelParents, ...standalone];
                    const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
                    parents.sort(byName);
                    Object.values(childrenByParent).forEach(arr => arr.sort(byName));
                    neighborhoodAccordionData[borough] = { parents, childrenByParent };
                }
                return {
                    neighborhoods: enhancedNeighborhoods,
                    neighborhoodsByBorough,
                    neighborhoodAccordionData,
                    lastFetched: Date.now(),
                };
            };
            const loading = get()?.loading;
            const response = await runWithDedup(dedupKey, async () => {
                return loading?.withLoading ? await loading.withLoading(key, exec) : await exec();
            });
            setSlice(prev => ({
                ...prev,
                neighborhoods: response.neighborhoods,
                neighborhoodsByBorough: response.neighborhoodsByBorough,
                neighborhoodAccordionData: response.neighborhoodAccordionData,
                lastFetched: response.lastFetched,
            }));
            return response;
        },
        filterByQuery: (query) => {
            const q = (query || '').toLowerCase().trim();
            const neighborhoodsByBorough = getSlice().neighborhoodsByBorough;
            if (!q)
                return neighborhoodsByBorough;
            // Check if we have any data
            const hasData = Object.keys(neighborhoodsByBorough).length > 0;
            if (!hasData)
                return {};
            const filtered = {};
            for (const [borough, list] of Object.entries(neighborhoodsByBorough)) {
                if (!Array.isArray(list) || list.length === 0)
                    continue;
                const matches = list.filter(n => n.name?.toLowerCase().includes(q));
                if (matches.length > 0)
                    filtered[borough] = matches;
            }
            return filtered;
        },
    }),
});
